// LoadingContext.jsx - Globalni loading modal za učitavanje/spremanje/brisanje

import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import Modal from 'react-modal';
import '../css/LoadingModal.css';

const LOADING_TIMEOUT_MS = 30000; // 30s safety - auto-dismiss da izbjegnemo infinite loop

export const LoadingContext = createContext();

export const LoadingProvider = ({ children }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('Učitavanje...');
  const counterRef = useRef(0);
  const timeoutRef = useRef(null);

  const clearTimeoutSafety = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const showLoading = useCallback((msg = 'Učitavanje...') => {
    counterRef.current += 1;
    setMessage(msg);
    setIsLoading(true);

    clearTimeoutSafety();
    timeoutRef.current = setTimeout(() => {
      if (counterRef.current > 0) {
        console.warn('Loading timeout - automatski zatvaranje');
        counterRef.current = 0;
        setIsLoading(false);
        clearTimeoutSafety();
      }
    }, LOADING_TIMEOUT_MS);
  }, [clearTimeoutSafety]);

  const hideLoading = useCallback(() => {
    counterRef.current = Math.max(0, counterRef.current - 1);
    if (counterRef.current === 0) {
      clearTimeoutSafety();
      setIsLoading(false);
    }
  }, [clearTimeoutSafety]);

  const withLoading = useCallback((asyncFn, msg = 'Učitavanje...') => {
    return async (...args) => {
      showLoading(msg);
      try {
        const result = await asyncFn(...args);
        return result;
      } finally {
        hideLoading();
      }
    };
  }, [showLoading, hideLoading]);

  return (
    <LoadingContext.Provider value={{ showLoading, hideLoading, withLoading, isLoading }}>
      {children}
      <Modal
        isOpen={isLoading}
        className="loading-modal"
        overlayClassName="loading-modal-overlay"
        contentLabel="Učitavanje"
        ariaHideApp={false}
      >
        <div className="loading-modal-content">
          <div className="loading-spinner" />
          <p className="loading-message">{message}</p>
        </div>
      </Modal>
    </LoadingContext.Provider>
  );
};

export const useLoading = () => useContext(LoadingContext);
